// src/client/components/ChatBox.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  addDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
  limit,
  doc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL
} from "firebase/storage";
import { db, storage } from "../../firebase";
import useCurrentUser from "../../hooks/useCurrentUser";
import useActiveUsers from "../../hooks/useActiveUsers";
import { Paperclip, Send, User, MoreVertical, Phone, Video, X, FileText, Download } from "lucide-react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function minutesApart(a, b) {
  if (!a || !b) return 999;
  return Math.abs((a.getTime() - b.getTime()) / 60000);
}

export default function ChatBox({ role = "Client", chatId = "client-operator" }) {
  const { clientId, loading } = useCurrentUser();
  const { operators, customers } = useActiveUsers(clientId);

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [fileMsg, setFileMsg] = useState(null); // { file, type, name }

  const [peerTyping, setPeerTyping] = useState(false);
  const meTypingRef = useRef(false);
  const listRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const otherRole = role.toLowerCase() === "client" ? "Operator" : "Client";

  // subscribe to messages (latest 200)
  useEffect(() => {
    const qRef = query(
      collection(db, "chats", chatId, "messages"),
      orderBy("createdAt", "asc"),
      limit(200)
    );
    const unsub = onSnapshot(qRef, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMessages(rows);
      if (isNearBottom()) scrollToBottom();
    });
    return () => unsub();
  }, [chatId]);

  // typing indicator: watch the other side
  useEffect(() => {
    const typingDoc = doc(db, "chats", chatId, "meta", `typing_${otherRole}`);
    const unsub = onSnapshot(typingDoc, (snap) => {
      const data = snap.data();
      setPeerTyping(Boolean(data?.isTyping));
    });
    return () => unsub();
  }, [chatId, otherRole]);

  // update my typing state
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!meTypingRef.current) return;
      await setDoc(
        doc(db, "chats", chatId, "meta", `typing_${role}`),
        { isTyping: true, at: serverTimestamp() },
        { merge: true }
      );
    }, 120);
    const clear = setTimeout(async () => {
      await setDoc(
        doc(db, "chats", chatId, "meta", `typing_${role}`),
        { isTyping: false, at: serverTimestamp() },
        { merge: true }
      );
      meTypingRef.current = false;
    }, 1500);
    return () => {
      clearTimeout(t);
      clearTimeout(clear);
    };
  }, [text, chatId, role]);

  const isNearBottom = () => {
    const el = listRef.current;
    if (!el) return true;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    return gap < 120;
  };
  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  const handleChange = (e) => {
    setText(e.target.value);
    meTypingRef.current = true;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileMsg({ file, name: file.name, type: file.type });
  };

  const sendMessage = async () => {
    const trimmed = text.trim();
    if ((!trimmed && !fileMsg) || sending) return;
    setSending(true);

    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      sender: role,
      text: trimmed,
      fileUrl: fileMsg ? URL.createObjectURL(fileMsg.file) : null,
      fileName: fileMsg?.name,
      fileType: fileMsg?.type,
      createdAt: { toDate: () => new Date() },
      _optimistic: true,
    };

    setMessages((m) => [...m, optimistic]);
    setText("");
    setFileMsg(null);
    scrollToBottom();

    try {
      let fileUrl = null;
      if (fileMsg) {
        // Upload file first
        const storageRef = ref(storage, `chats/${chatId}/${Date.now()}_${fileMsg.name}`);
        const uploadTask = await uploadBytesResumable(storageRef, fileMsg.file);
        fileUrl = await getDownloadURL(uploadTask.ref);
      }

      await addDoc(collection(db, "chats", chatId, "messages"), {
        sender: role,
        text: trimmed,
        fileUrl: fileUrl,
        fileName: fileMsg?.name || null,
        fileType: fileMsg?.type || null,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Failed to send message", err);
      // Could allow retry logic here
    } finally {
      setSending(false);
      setTimeout(scrollToBottom, 50);
    }
  };

  const groups = useMemo(() => {
    const out = [];
    let lastDay = null;
    let run = null;

    for (const m of messages) {
      const dt =
        typeof m.createdAt?.toDate === "function"
          ? m.createdAt.toDate()
          : (m.createdAt instanceof Date ? m.createdAt : null);

      if (!lastDay || !isSameDay(lastDay, dt || new Date())) {
        if (run) out.push(run);
        out.push({ type: "day", date: dt || new Date(), id: `d-${m.id}` });
        lastDay = dt || new Date();
        run = null;
      }

      if (
        !run ||
        run.sender !== m.sender ||
        minutesApart(run.lastTime, dt || new Date()) > 5
      ) {
        if (run) out.push(run);
        run = {
          type: "run",
          sender: m.sender,
          items: [],
          lastTime: dt || new Date(),
          id: `r-${m.id}`,
        };
      }

      run.items.push({ ...m, time: dt || new Date() });
      run.lastTime = dt || new Date();
    }
    if (run) out.push(run);
    return out;
  }, [messages]);

  if (loading) return <div className="p-12 text-center text-slate-500">Loading chat environment...</div>;

  return (
    <div className="flex h-[calc(100vh-140px)] gap-6 p-2 max-w-[1600px] mx-auto">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-navy-900 flex items-center justify-center text-white font-serif font-bold">
              {otherRole === "Operator" ? "OP" : "CL"}
            </div>
            <div>
              <h2 className="text-navy-900 font-bold text-lg leading-tight">
                {otherRole === "Operator" ? "Support Operator" : "Client Interaction"}
              </h2>
              <div className="text-xs text-slate-500 flex items-center gap-1 h-4">
                {peerTyping ? (
                  <span className="text-brand-gold animate-pulse font-medium">Typing...</span>
                ) : (
                  <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Online</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-slate-400">
            <button className="p-2 hover:bg-slate-200 rounded-full transition-colors"><Phone size={18} /></button>
            <button className="p-2 hover:bg-slate-200 rounded-full transition-colors"><Video size={18} /></button>
            <button className="p-2 hover:bg-slate-200 rounded-full transition-colors"><MoreVertical size={18} /></button>
          </div>
        </div>

        {/* Message List */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto px-6 py-6 bg-slate-50 space-y-4"
        >
          {groups.map((g) =>
            g.type === "day" ? (
              <div key={g.id} className="flex items-center gap-4 my-6">
                <div className="h-px flex-1 bg-slate-200" />
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                  {g.date.toLocaleDateString([], { month: "short", day: "numeric" })}
                </div>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
            ) : (
              <div
                key={g.id}
                className={`flex gap-3 ${g.sender === role ? "justify-end" : "justify-start"}`}
              >
                {g.sender !== role && (
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex-shrink-0 flex items-center justify-center text-xs text-slate-500 font-bold border-2 border-white shadow-sm mt-1">
                    {g.sender[0]}
                  </div>
                )}
                <div className={`max-w-[70%] space-y-1`}>
                  {g.items.map((m, i) => (
                    <div
                      key={m.id || i}
                      className={`px-4 py-3 text-sm shadow-sm ${g.sender === role
                        ? "bg-navy-900 text-white rounded-2xl rounded-tr-sm"
                        : "bg-white text-slate-700 border border-slate-100 rounded-2xl rounded-tl-sm"
                        } ${i === 0 ? "" : "rounded-t-2xl"} ${i === g.items.length - 1 ? "" : "rounded-b-2xl"
                        }`}
                    >
                      {/* File Attachment */}
                      {m.fileUrl && (
                        <div className="mb-2">
                          {m.fileType?.startsWith("image/") ? (
                            <img src={m.fileUrl} alt="attachment" className="rounded-lg max-h-48 object-cover border border-white/20" />
                          ) : (
                            <a href={m.fileUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-2 rounded-lg ${g.sender === role ? "bg-white/10 hover:bg-white/20" : "bg-slate-100 hover:bg-slate-200"} transition-colors`}>
                              <FileText size={20} />
                              <span className="underline truncate max-w-[150px]">{m.fileName || "Attachment"}</span>
                              <Download size={14} className="ml-1" />
                            </a>
                          )}
                        </div>
                      )}

                      {m.text && (
                        <div className={`leading-relaxed break-words ${g.sender === role ? "prose prose-invert" : "prose prose-slate"} prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-headings:my-2 prose-headings:text-inherit prose-a:text-blue-400`}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {m.text}
                          </ReactMarkdown>
                        </div>
                      )}

                      <div className={`text-[10px] mt-1 flex items-center justify-end gap-1 ${g.sender === role ? "text-slate-300" : "text-slate-400"}`}>
                        {m.time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {m._optimistic && <span className="animate-pulse">· sending</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
          <div ref={bottomRef} />
        </div>

        {/* Composer */}
        <div className="p-4 bg-white border-t border-slate-100">
          {fileMsg && (
            <div className="flex items-center justify-between bg-slate-50 px-4 py-2 rounded-lg mb-2 border border-slate-200">
              <div className="flex items-center gap-2 text-sm text-navy-900">
                <Paperclip size={16} className="text-gold-500" />
                <span className="font-medium truncate max-w-xs">{fileMsg.name}</span>
              </div>
              <button onClick={() => setFileMsg(null)} className="text-slate-400 hover:text-red-500"><X size={16} /></button>
            </div>
          )}
          <div className="flex items-end gap-3">
            <label className="p-3 text-slate-400 hover:text-navy-900 hover:bg-slate-50 rounded-full cursor-pointer transition-colors">
              <Paperclip size={20} />
              <input type="file" className="hidden" onChange={handleFileSelect} />
            </label>
            <textarea
              ref={inputRef}
              value={text}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Write your message..."
              rows={1}
              className="flex-1 resize-none px-4 py-3 rounded-xl bg-slate-50 text-navy-900 border-0 focus:ring-2 focus:ring-navy-900/10 focus:bg-white transition-all placeholder:text-slate-400"
              style={{ minHeight: 48, maxHeight: 120 }}
            />
            <button
              onClick={sendMessage}
              disabled={(!text.trim() && !fileMsg) || sending}
              className="p-3 bg-navy-900 hover:bg-navy-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl shadow-lg shadow-navy-900/20 transition-all transform active:scale-95"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Partners Place Sidebar */}
      <aside className="hidden md:flex flex-col w-80 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 bg-slate-50">
          <h3 className="text-navy-900 font-bold text-lg font-serif">Partners Place</h3>
          <p className="text-xs text-slate-500 mt-1">Active team members and operators.</p>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-6">
          {/* Operators */}
          <div>
            <div className="flex items-center justify-between mb-3 px-2">
              <h4 className="text-xs font-bold text-gold-600 uppercase tracking-wider">Operators</h4>
              <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-[10px] font-bold">{operators.length}</span>
            </div>
            <div className="space-y-2">
              {operators.length === 0 ? (
                <p className="text-sm text-slate-400 px-2 italic">No operators online</p>
              ) : (
                operators.map((u) => (
                  <div key={u.email} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer group">
                    <div className="relative">
                      <div className="w-8 h-8 rounded-full bg-navy-900 flex items-center justify-center text-white text-xs font-bold">
                        {u.email[0].toUpperCase()}
                      </div>
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full"></span>
                    </div>
                    <div className="overflow-hidden">
                      <p className="text-sm font-medium text-navy-900 truncate group-hover:text-blue-600 transition-colors">{u.email}</p>
                      <p className="text-[10px] text-slate-400">Online now</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Customers */}
          <div>
            <div className="flex items-center justify-between mb-3 px-2">
              <h4 className="text-xs font-bold text-gold-600 uppercase tracking-wider">Customers</h4>
              <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-[10px] font-bold">{customers.length}</span>
            </div>
            <div className="space-y-2">
              {customers.length === 0 ? (
                <p className="text-sm text-slate-400 px-2 italic">No customers online</p>
              ) : (
                customers.map((u) => (
                  <div key={u.email} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer">
                    <div className="relative">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-bold">
                        {u.email[0].toUpperCase()}
                      </div>
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full"></span>
                    </div>
                    <div className="overflow-hidden">
                      <p className="text-sm font-medium text-slate-700 truncate">{u.email}</p>
                      <p className="text-[10px] text-slate-400">Active</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-100 text-center">
          <button className="text-xs text-navy-600 hover:text-navy-900 font-medium underline">Invite new partner</button>
        </div>
      </aside>
    </div>
  );
}
